import Vue from 'utils/vueUtils';
import { debounce } from 'lodash';
import Conversations from '../collections/Conversations';
import Conversation from '../models/Conversation';
import Contact from '../models/Contact';
import { TYPES_INBOX } from '../modules/user';
import { baseApi } from 'core/api';

const debounces = {};

const state = () => ({
    _items: {},
    _item: null,
    _order: [],
    _pagination: {
        hasMore: true,
    },
    _toolboxTopHeight: 48,
    _parameters: {
        page: 1,
        sortOrder: 'desc',
        search: null,
        limit: 15,
        moreLimit: 25,
    },
    _usersTyping: [],
    _loadingRead: false,
    _loadingUnread: false,
    _conversation: null,
    _bulk_all: false,
    _switchV2: false,
    _filterKey: 'open',
    _filterList: [],
    _filterListLoading: null,
    _orderFilterKey: 'newest',
    _orderFilterList: [],
    _orderFilterListLoading: null,
    _profileState: {},
    _newSmartInboxNumber: null,
});

const getters = {
    conversation: (state) => state._item,
    conversations: (state) => state._items,
    conversationsArray: (state) => {
        const array = [];

        for (let key in state._items) {
            array.push(state._items[key]);
        }

        return array;
    },
    conversationsParameters: (state) => state._parameters,
    toolboxTopHeight: (state) => state._toolboxTopHeight,
    conversationsPagination: (state) => state._pagination,
    conversationsOrder: (state) => [...new Set(state._order)],
    usersTyping: (state) => state._usersTyping,
    loadingRead: (state) => state._loadingRead,
    loadingUnread: (state) => state._loadingUnread,
    conversationsBulkAll: (state) => state._bulk_all,
    conversationsSwitchV2: (state) => state._switchV2,
    conversationsFilter: (state) => state._filterKey,
    conversationFilterLabel: (state) => {
        const filter = state._filterList.find(({ key }) => key === state._filterKey);

        return filter?.label;
    },
    conversationsFilterList: (state) => state._filterList,
    conversationsFilterListLoading: (state) => {
        if (state._filterListLoading === null) return true;

        return state._filterListLoading;
    },
    conversationsOrderFilter: (state) => state._orderFilterKey,
    conversationOrderLabel: (state) => {
        const filter = state._orderFilterList.find(({ key }) => key === state._orderFilterKey);

        return filter?.label;
    },
    conversationsOrderFilterList: (state) => state._orderFilterList,
    conversationsOrderFilterListLoading: (state) => {
        if (state._orderFilterListLoading === null) return true;

        return state._orderFilterListLoading;
    },
    conversationProfileState: (state) => state._profileState,
    newSmartInboxNumber: (state) => state._newSmartInboxNumber,
};

const utils = {
    userTypingRemove: (user, storeInstance) => {
        storeInstance._setConversationUserTypingRemove(user);
        delete debounces[user.id];
    },

    deleteConversationOrder: (storeInstance, conversationId) => {
        if (typeof conversationId !== 'number') return;

        const existIndex = storeInstance._order.findIndex((orderId) => conversationId === orderId);

        if (existIndex !== -1) {
            storeInstance._order.splice(existIndex, 1);
        }
    },

    addConversationOrder: (storeInstance, conversationId) => {
        if (typeof conversationId !== 'number') return;

        const exist = storeInstance._order.find((orderId) => conversationId === orderId);

        if (!exist) {
            storeInstance._order.push(conversationId);
        }
    },

    updateConversationOrder: (storeInstance, { conversationId, index = 0 }) => {
        if (typeof conversationId !== 'number') return;

        const conversation = storeInstance._items[conversationId];
        const countItems = storeInstance._order.length;
        const countIsFavorite = storeInstance._order.reduce((s, id) => {
            const item = storeInstance._items[id];

            return item?.is_favorite ? s + 1 : s;
        }, 0);

        const existIndex = storeInstance._order.findIndex((orderId) => conversationId === orderId);

        if (storeInstance._orderFilterKey === 'priority') {
            if (!conversation?.is_favorite) {
                index += countIsFavorite;
            }
        }

        if (storeInstance._orderFilterKey === 'oldest') {
            index += countItems;
        }

        if (existIndex !== -1) {
            storeInstance._order.splice(existIndex, 1);
        }

        storeInstance._order.splice(index, 0, conversationId);
    },
};

const userTypingRemoveDebounce = debounce(utils.userTypingRemove, 2000);

const actions = {
    userTypingAdd(user) {
        this._setConversationUserTypingAdd(user);
    },

    userTypingRemove(user) {
        if (!debounces[user.id]) {
            debounces[user.id] = userTypingRemoveDebounce;
            debounces[user.id](user, this);
        } else {
            debounces[user.id](user, this);
        }
    },

    updateConversationsByIdSwitch(id) {
        return baseApi.post(`core/conversations/${id}/switch`).then((response) => {
            return response.data;
        });
    },

    getConversationsDowngradeStatistic() {
        return baseApi.get('core/conversations/downgrade-statistic').then((response) => {
            return response.data;
        });
    },

    onForwardMessage(forwardData) {
        return baseApi.post('core/messages/forward', forwardData);
    },

    updateOrder({ id, type = 'delete' }) {
        this._setConversationOrderUpdate({ id, type });
    },

    updateConversationSwitchV2({ status }) {
        this._setConversationSwitchV2({ status });
    },

    getConversations({ params, isMore = false, grouped_response = true }) {
        params = {
            ...params,
            grouped_response: grouped_response,
        };

        if (params.filter === 'scheduled' || params.filter === 'draft') {
            params.filter = 'open';
        }

        return baseApi.get('core/conversations', { params }).then((response) => {
            let conversations = [];
            const data = response.data;
            const contacts = data.contacts;
            const numbers = data.numbers;
            const owners = data.owners;

            if (grouped_response) {
                conversations = data.conversations.map((item) => {
                    item.contact = contacts[item.contact_id] || null;
                    item.number = numbers[item.number_id] || null;
                    item.owner = owners[item.user_id] || null;

                    return item;
                });
            } else {
                conversations = data;
            }

            const items = new Conversations(conversations).models;

            this._setConversationsLoaded({
                conversations: items,
                isMore: isMore,
            });

            return items;
        });
    },

    resetConversations() {
        this._setConversationsReset();
    },

    deleteConversationsBatchClose(teamId) {
        const params = {
            team_id: teamId,
        };

        return baseApi.delete('core/conversations/batch/close', { params }).then((response) => {
            return response.data;
        });
    },

    getConversationsById({ id, update = true, forceUpdate = false, cancelToken = null }) {
        return baseApi.get(`core/conversations/${id}`, { cancelToken }).then((response) => {
            const item = new Conversation(response.data);

            if (update || forceUpdate) {
                this._setConversationLoaded({
                    conversation: item,
                    forceUpdate: forceUpdate,
                });
            }

            return item;
        });
    },

    createConversation({
        contactId,
        teamId,
        numberId,
        participants = [],
        integrationPayload = null,
    }) {
        let body = {
            contact_id: contactId,
            participants: participants,
        };

        const number = Number(numberId);

        if (number) {
            body.number_id = number;
        }

        if (teamId) {
            body.team_id = teamId;
        }

        if (integrationPayload) {
            body.integration = integrationPayload;
        }

        return baseApi.post('core/conversations', body).then((response) => {
            return new Conversation(response.data);
        });
    },

    updateConversationsByIdClose(id) {
        return baseApi.put(`core/conversations/${id}/close`).then(async (response) => {
            const conversation = new Conversation(response.data);
            this._setConversationDelete(id);
            return conversation;
        });
    },

    updateConversationsByIdCloseWithoutDelete(id) {
        return baseApi.put(`core/conversations/${id}/close`).then(async (response) => {
            return new Conversation(response.data);
        });
    },

    updateConversationsBatchQueueClose({ ids, teamId, deleteAll, except }) {
        const body = { ids };

        body.team_id = teamId || 0;

        // commented for disable close all function
        if (deleteAll) body.delete_all = deleteAll;

        return baseApi.put('core/conversations/batch-queue/close', body).then((response) => {
            return response;
        });
    },

    updateConversationsByIdOpen(id) {
        return baseApi.put(`core/conversations/${id}/open`).then((response) => {
            return new Conversation(response.data);
        });
    },

    bulkCloseConversations(ids) {
        ids.forEach((id) => this._setConversationDelete(id));
    },

    setConversationsBulkAll(bulkAllStatus) {
        this._setConversationsBulkAll(bulkAllStatus);
    },

    changeToolboxTopHeight(height) {
        this._setChangeToolboxHeight(height);
    },

    updateConversationsByIdReassign({ id, params }) {
        return baseApi.post(`core/conversations/${id}/reassign`, params).then(async (response) => {
            const conversation = new Conversation(response.data);
            conversation.contact = this.conversations[id].contact;
            this._setConversationUpdateOrAdd({ conversation });
            return conversation;
        });
    },

    updateConversationsByIdParticipants({ id, params, existContacts = [] }) {
        return baseApi
            .post(`core/conversations/${id}/participants`, params)
            .then(async (response) => {
                const contacts = existContacts;
                const newContacts = response.data.map((item) => new Contact(item.contact));

                newContacts.forEach((contact) => {
                    const exist = contacts.find((item) => item.id === contact.id);

                    if (!exist) {
                        contacts.push(contact);
                    }
                });

                this._setConversationUpdateOrAdd({
                    conversation: {
                        id: id,
                        participants: contacts,
                    },
                    updateProps: true,
                });

                return contacts;
            });
    },

    updateConversationsById({ id, params }) {
        return baseApi.put(`core/conversations/${id}`, params).then(async (response) => {
            const conversation = new Conversation(response.data);
            this._setConversationUpdateOrAdd({ conversation });
            return conversation;
        });
    },

    updateConversationsByIdRead({ id }) {
        if (this._loadingRead) return;

        this._setConversationReadLoading(true);

        return baseApi
            .put(`core/conversations/${id}/read`)
            .then(async (response) => {
                const conversation = new Conversation(response.data);

                conversation.has_new_message = false;

                this._setConversationUpdate({
                    conversation: conversation,
                    updateProps: [
                        'contact',
                        'unread_count',
                        'last_manual_message_at',
                        'last_message_at',
                        'last_read_at',
                        'has_new_message',
                    ],
                    excludeProps: [
                        'draft_message',
                        'hasCurrentSocket',
                        'draftInitializedForm',
                        'debounceNotifyDrafting',
                    ],
                });

                return conversation;
            })
            .finally(() => {
                this._setConversationReadLoading(false);
            });
    },

    updateConversationsByIdUnread({ id, force = false }) {
        if (this._loadingUnread && !force) return;

        this._setConversationUnreadLoading(true);

        return baseApi
            .put(`core/conversations/${id}/unread`)
            .then(async (response) => {
                const conversation = new Conversation(response.data);

                conversation.contact.custom_fields = await this.getContactsCustomFieldsById(
                    conversation.contact
                );
                conversation.has_new_message = false;

                this._setConversationUpdate({
                    conversation: conversation,
                    updateProps: [
                        'contact',
                        'unread_count',
                        'last_manual_message_at',
                        'last_message_at',
                        'last_read_at',
                        'has_new_message',
                    ],
                    excludeProps: [
                        'draft_message',
                        'hasCurrentSocket',
                        'draftInitializedForm',
                        'debounceNotifyDrafting',
                    ],
                });

                return conversation;
            })
            .finally(() => {
                this._setConversationUnreadLoading(false);
            });
    },

    getConversationsByIdExport({ id, params = {} }) {
        return baseApi.get(`core/conversations/${id}/export`, { params }).then(async (response) => {
            return response.data;
        });
    },

    getConversationsOrderFiltersList(params = {}) {
        if (this._orderFilterListLoading !== null) return;

        this._setConversationStateUpdate({
            orderFilterListLoading: true,
        });

        return baseApi
            .get('core/conversations/orders/list', {
                params: params,
            })
            .then((response) => {
                this._setConversationStateUpdate({
                    orderFilterList: response.data,
                });

                return response.data;
            })
            .finally(() => {
                this._setConversationStateUpdate({
                    orderFilterListLoading: false,
                });
            });
    },

    getConversationsFiltersList(params = {}) {
        if (this._filterListLoading !== null) return;

        this._setConversationStateUpdate({
            filterListLoading: true,
        });

        return baseApi
            .get('core/conversations/filters/list', {
                params: params,
            })
            .then((response) => {
                let filterList = response.data;

                filterList = filterList
                    .map((item) => {
                        if (item.key === 'open') {
                            item.inboxes = true;
                            item.personal = true;
                            item.enableCount = false;
                            item.icon = 'inbox';
                        }

                        if (item.key === 'unread') {
                            item.inboxes = true;
                            item.personal = true;
                            item.enableCount = false;
                            item.icon = 'inbox-in';
                        }

                        if (item.key === 'pending') {
                            item.inboxes = true;
                            item.personal = true;
                            item.enableCount = false;
                            item.type = 'vector';
                            item.icon = 'flag';
                        }

                        if (item.key === 'assigned') {
                            item.inboxes = true;
                            item.personal = false;
                            item.enableCount = false;
                            item.icon = 'clipboard-check';
                        }

                        if (item.key === 'mentions') {
                            item.inboxes = true;
                            item.personal = false;
                            item.enableCount = false;
                            item.type = 'vector';
                            item.icon = 'mentions';
                        }

                        if (item.key === 'unassigned') {
                            item.inboxes = true;
                            item.personal = false;
                            item.enableCount = false;
                            item.icon = 'question';
                        }

                        if (item.key === 'scheduled') {
                            item.inboxes = true;
                            item.personal = true;
                            item.enableCount = false;
                            item.hasGrey = true;
                            item.icon = 'clock';
                        }

                        if (item.key === 'draft') {
                            item.inboxes = true;
                            item.personal = true;
                            item.enableCount = false;
                            item.hasGrey = true;
                            item.icon = 'comment';
                        }

                        if (item.key === 'outbound') {
                            item.inboxes = true;
                            item.personal = true;
                            item.icon = 'inbox-out';
                        }

                        if (item.key === 'closed') {
                            item.inboxes = true;
                            item.personal = true;
                            item.icon = 'check';
                        }

                        return item;
                    })
                    .filter((o) => (this.inbox.id === 0 ? o.personal : o.inboxes));

                this._setConversationStateUpdate({
                    filterList: filterList,
                });

                return filterList;
            })
            .finally(() => {
                this._setConversationStateUpdate({
                    filterListLoading: false,
                });
            });
    },

    deleteConversation(id) {
        this._setConversationDelete(id);
        this._setConversationDeleteOrder(id);
    },

    updateContactByConversation({ id, contact }) {
        this._setConversationUpdateContact({
            id,
            contact,
        });
    },

    updateOrAddConversation({
        conversation,
        updateProps = false,
        updateOrder = false,
        excludeProps = [],
    }) {
        this._setConversationUpdateOrAdd({
            conversation,
            updateProps,
            updateOrder,
            excludeProps,
        });
    },

    updateConversation({
        conversation,
        updateProps = false,
        updateOrder = false,
        excludeProps = [],
    }) {
        this._setConversationUpdate({
            conversation,
            updateProps,
            updateOrder,
            excludeProps,
        });
    },

    initConversation(conversation) {
        this._setConversationInit(conversation);
    },

    createEmptyConversation({ conversation = {} }) {
        this._setConversationCreateEmpty(conversation);
    },

    createEmptyConversationForWidget({ data = {} }) {
        const conversation = new Conversation({
            id: 0,
            closed_at: '',
            contact: {
                opt_out: null,
            },
            participants: [],
            contact_id: 0,
            inbox_id: 0,
            last_message_at: moment().valueOf(),
            last_read_at: '',
            number_id: 0,
            number: {},
            owner: null,
            reassign_logs: [],
            type: '',
            recent_message: {
                body: '',
                id: 0,
                sent_at: '',
                status: '',
                type: '',
                user_id: 0,
            },
            started_at: '',
            user_id: 0,
            ...data,
        });

        this._setConversationCreateEmptyForWidget(conversation);
        return conversation;
    },

    updateEmptyConversation({ conversation = {} }) {
        this._setConversationUpdateEmpty(conversation);
    },

    updateStateConversations(data = {}) {
        this._setConversationStateUpdate(data);
    },

    updateStateProfileConversations(data = {}) {
        this._setConversationStateProfileUpdate(data);
    },

    setNewSmartInboxNumber(number) {
        this._setConversationSetNewSmartInboxNumber(number);
    },

    setUnreadConversation(data) {
        this._setConversationStateProfileUpdate(data);
    },

    updateConversationsFavorite({ id, status }) {
        return baseApi.post('core/conversations/favorite', {
            conversation_id: id,
            favorite: status,
        });
    },

    setFilterKey(key) {
        this._setConversationSetFilterKey(key);
    },

    setFilterOrderKey(key) {
        this._setConversationSetFilterOrderKey(key);
    },

    addConversationOrder(id) {
        this._setConversationAddOrder(id);
    },

    updateConversationOrder({ id, index = 0 }) {
        this._setConversationUpdateOrder({
            conversationId: id,
            index: index,
        });
    },

    deleteConversationOrder({ id, index = 0 }) {
        this._setConversationDeleteOrder(id);
    },

    _setMessageDeletedDraft(id) {
        let conversation = this._items[id];

        if (conversation) {
            conversation = conversation.update({
                draft_message: null,
            });

            Vue.set(this._items, conversation.id, conversation);
        }

        if (this._item && this._item.id === id) {
            this._item = conversation.update({
                draft_message: null,
            });
        }
    },

    _setConversationUpdate({ conversation, updateProps, updateOrder, excludeProps = [] }) {
        if (this._items[conversation.id]) {
            if (updateProps) {
                conversation = this._items[conversation.id].update(
                    conversation,
                    excludeProps,
                    updateProps
                );

                this._items[conversation.id] = conversation;
            } else {
                conversation = this._items[conversation.id].update(conversation);

                this._items[conversation.id] = conversation;

                this._items = { ...this._items, [conversation.id]: conversation };
            }

            if (this._item && this._item.id === conversation.id)
                this._item = this._items[conversation.id];

            if (updateOrder) {
                utils.updateConversationOrder(this, {
                    conversationId: conversation.id,
                });
            }
        }
    },

    // Private methods for all operations
    _setConversationStateProfileUpdate(data) {
        for (let key in data) {
            Vue.set(this._profileState, key, data[key]);
        }
    },

    _setConversationStateUpdate(data) {
        for (let key in data) {
            this[key] = data[key];
        }
    },

    _setConversationSwitchV2({ status }) {
        this._switchV2 = status;
    },

    _setChangeToolboxHeight(height) {
        this._toolboxTopHeight = height;
    },

    _setConversationUpdateEmpty(conversation) {
        if (this._items[0]) {
            delete this._items[0];
            utils.deleteConversationOrder(this, 0);
        }

        this._item = conversation;
        Vue.set(this._items, conversation.id, conversation);
        utils.updateConversationOrder(this, {
            conversationId: conversation.id,
        });
    },

    _setConversationCreateEmpty(data) {
        if (this._items[0]) {
            const conversation = new Conversation({
                ...this._items[0],
                ...data,
                last_message_at: moment().valueOf(),
            });

            Vue.set(this._items, 0, conversation);
            this._item = conversation;
            utils.updateConversationOrder(this, {
                conversationId: conversation.id,
            });
        } else {
            const conversation = new Conversation({
                id: 0,
                closed_at: '',
                contact: {
                    opt_out: null,
                },
                participants: [],
                contact_id: 0,
                inbox_id: 0,
                last_message_at: moment().valueOf(),
                last_read_at: '',
                number_id: 0,
                number: {},
                owner: null,
                reassign_logs: [],
                type: '',
                recent_message: {
                    body: '',
                    id: 0,
                    sent_at: '',
                    status: '',
                    type: '',
                    user_id: 0,
                },
                started_at: '',
                user_id: 0,
                ...data,
            });

            this._item = conversation;
            this._items[conversation.id] = conversation;
            utils.updateConversationOrder(this, {
                conversationId: conversation.id,
            });
        }
    },

    _setConversationCreateEmptyForWidget(conversation) {
        this._items[conversation.id] = conversation;
        this._item = conversation;
    },

    _setConversationUpdateContact({ id, contact }) {
        if (!id) {
            for (let key in this._items) {
                const item = this._items[key];
                item.updateParticipants(contact);
                Vue.set(this._items, item.id, item);
            }
            return;
        }

        if (this._items[id]) {
            const conversation = this._items[id];
            conversation.updateParticipants(contact);
            Vue.set(this._items, id, conversation);
            this._item = conversation;
        }
    },

    _setContactUpdated({ contact, props = null }) {
        for (let key in this._items) {
            const conversation = this._items[key];
            const conversationContact = this._items[key].contact;

            if (conversationContact.id === contact.id) {
                conversation.updateParticipants(contact, props);
                Vue.set(this._items, conversation.id, conversation);
            }
        }
    },

    _setConversationOrderUpdate({ id, type }) {
        if (type === 'delete') {
            utils.deleteConversationOrder(this, id);
            delete this._items[id];
        }
    },

    _setConversationsReset() {
        this._item = null;
        this._items = {};
        this._order = [];
        this._pagination.hasMore = false;
    },

    _setConversationsLoaded({
        conversations,
        isMore,
        isSearch = false,
        page,
        setPagination = false,
    }) {
        if (isSearch) {
            if (page === 1) {
                this._items = {};
                this._order = [];
                this._item = conversations.length ? conversations[0] : null;
            }
        }

        if (isMore) {
            this._pagination.hasMore = isSearch
                ? isMore
                : conversations.length === this._parameters.moreLimit;

            conversations.forEach((item) => {
                if (this._items[item.id]) return;
                this._items[item.id] = item;
                utils.addConversationOrder(this, item.id);
            });
        } else if (this._redirectToConversation) {
            this._pagination.hasMore = isSearch
                ? isMore
                : conversations.length === this._parameters.moreLimit;

            conversations.forEach((item) => {
                if (this._items[this._redirectToConversation]) return;
                this._items[item.id] = item;
                utils.addConversationOrder(this, item.id);
            });

            utils.updateConversationOrder(this, {
                conversationId: this._redirectToConversation,
            });
        } else {
            this._pagination.hasMore = isSearch
                ? isMore
                : conversations.length === this._parameters.limit;

            conversations.forEach((item) => {
                this._items[item.id] = item;
                utils.addConversationOrder(this, item.id);
            });
        }

        if (setPagination) {
            this._pagination.hasMore = isMore;
        }
    },

    _setConversationLoaded({ conversation, forceUpdate }) {
        if (
            this.currentInboxType !== TYPES_INBOX.UNIFIED &&
            this.currentInboxType !== TYPES_INBOX.GROUP &&
            this.currentInboxTypeId &&
            conversation.inbox_id !== this.currentInboxTypeId &&
            !forceUpdate
        )
            return;

        this._item = conversation;

        if (!this._items[conversation.id]) {
            this._items[conversation.id] = conversation;
            utils.updateConversationOrder(this, {
                conversationId: conversation.id,
            });
        }
    },

    _setConversationUpdateOrAdd({ conversation, updateProps, updateOrder, excludeProps = [] }) {
        if (this._items[conversation.id]) {
            if (updateProps) {
                conversation = this._items[conversation.id].update(
                    conversation,
                    excludeProps,
                    updateProps
                );
            } else {
                conversation = this._items[conversation.id].update(conversation, excludeProps);
            }

            Vue.set(this._items, conversation.id, conversation);

            if (this._item?.id === conversation.id) this._item = this._items[conversation.id];

            if (updateOrder) {
                utils.updateConversationOrder(this, {
                    conversationId: conversation.id,
                });
            }
        } else if (!updateProps) {
            this._items[conversation.id] =
                conversation instanceof Conversation
                    ? conversation
                    : new Conversation(conversation);
            if (this._item && this._item.id === conversation.id)
                this._item = this._items[conversation.id];
            utils.updateConversationOrder(this, {
                conversationId: conversation.id,
            });
        }
    },

    _setConversationAddOrder(conversationId) {
        utils.addConversationOrder(this, conversationId);
    },

    _setConversationUpdateOrder(data) {
        utils.updateConversationOrder(this, data);
    },

    _setConversationDeleteOrder(conversationId) {
        utils.deleteConversationOrder(this, conversationId);
    },

    _setConversationDelete(id) {
        if (this._items[id]) {
            delete this._items[id];

            if (this._item && this._item.id === id) this._item = null;

            utils.deleteConversationOrder(this, id);
        }
    },

    _setConversationInit(conversation) {
        this._item = conversation;

        if (!conversation) return;
        if (!this._items[conversation.id]) {
            this._items[conversation.id] = conversation;
            utils.updateConversationOrder(this, {
                conversationId: conversation.id,
            });
        }
    },

    _setConversationSet(conversation) {
        this._conversation = conversation;
    },

    _setConversationReadLoading(status) {
        this._loadingRead = status;
    },

    _setConversationUnreadLoading(status) {
        this._loadingUnread = status;
    },

    _setConversationUserTypingAdd(user) {
        if (this._usersTyping.findIndex((o) => o.id === user.id) === -1) {
            this._usersTyping.push(user);
        }
    },

    _setConversationUserTypingRemove(user) {
        this._usersTyping = this._usersTyping.filter((o) => o.id !== user.id);
    },

    _setConversationsBulkAll(bulkAllStatus) {
        this._bulk_all = bulkAllStatus;
    },

    _setConversationSetNewSmartInboxNumber(number) {
        this._newSmartInboxNumber = number;
    },

    _setConversationSetFilterKey(key) {
        this._filterKey = key;
    },

    _setConversationSetFilterOrderKey(key) {
        this._orderFilterKey = key;
    },
};

export default {
    state,
    getters,
    actions,
};
